import b4a from 'b4a'
import z32 from 'z32'
import * as C from './primitives.js'

const VERSION = 1
const ROOT_CONTEXT = 'pine/identity/v1'

/**
 * A Pine account is one Ed25519 keypair derived from a 32-byte seed.
 * The seed never leaves the device unencrypted; it is the only thing a user
 * needs to restore their account, so it doubles as the recovery key.
 *
 * Device sub-keys are derived from the root and carry a signed delegation,
 * which lets a second device write to the user's cores without ever holding
 * the root secret.
 */
export class Identity {
  constructor (seed, profile = {}) {
    this.seed = C.toBuffer(seed)
    if (this.seed.byteLength !== C.SEED_BYTES) throw new Error('Seed must be 32 bytes')

    const kp = C.signKeyPairFromSeed(C.domainHash(ROOT_CONTEXT, this.seed))
    this.publicKey = kp.publicKey
    this.secretKey = kp.secretKey
    this.boxPublicKey = C.toBoxPublicKey(this.publicKey)
    this.boxSecretKey = C.toBoxSecretKey(this.secretKey)

    // Keep whatever the caller stored. Destructuring a fixed set here used to
    // silently drop fields added later — the username survived a save and then
    // vanished on the next launch.
    this.profile = { name: '', avatar: null, bio: '', username: '', ...profile }
  }

  static generate (profile) {
    return new Identity(C.randomBytes(C.SEED_BYTES), profile)
  }

  static fromRecoveryKey (phrase, profile) {
    return new Identity(decodeRecoveryKey(phrase), profile)
  }

  /** Public, shareable account id, e.g. "pine:8f4h...". */
  get id () {
    return z32.encode(this.publicKey)
  }

  /** Short form for UI display. */
  get shortId () {
    const id = this.id
    return id.slice(0, 6) + '…' + id.slice(-4)
  }

  get recoveryKey () {
    return encodeRecoveryKey(this.seed)
  }

  sign (message) {
    return C.sign(message, this.secretKey)
  }

  verify (message, signature, publicKey = this.publicKey) {
    return C.verify(message, signature, publicKey)
  }

  /** Encrypt to someone else's account id. Only they can open it. */
  sealTo (message, recipientPublicKey) {
    return C.seal(message, C.toBoxPublicKey(toKey(recipientPublicKey)))
  }

  open (ciphertext) {
    return C.sealOpen(ciphertext, this.boxPublicKey, this.boxSecretKey)
  }

  /**
   * Deterministic keypair for a named purpose. Used for per-room writer cores
   * so that a user's rooms are not linkable to each other by key.
   */
  deriveKeyPair (purpose, salt = null) {
    const material = salt
      ? C.domainHash('pine/derive/' + purpose, this.seed, toKey(salt))
      : C.domainHash('pine/derive/' + purpose, this.seed)
    return C.signKeyPairFromSeed(material)
  }

  /** Authorise a device key. The delegation is public and verifiable by peers. */
  delegateDevice (devicePublicKey, { expires = 0, label = '' } = {}) {
    const payload = deviceDelegationPayload(this.publicKey, toKey(devicePublicKey), expires, label)
    return {
      account: this.publicKey,
      device: toKey(devicePublicKey),
      expires,
      label,
      signature: this.sign(payload)
    }
  }

  static verifyDelegation (d) {
    if (!d || !d.account || !d.device || !d.signature) return false
    if (d.expires && d.expires < Date.now()) return false
    const payload = deviceDelegationPayload(d.account, d.device, d.expires || 0, d.label || '')
    return C.verify(payload, d.signature, d.account)
  }

  /** Encrypted, password-protected export of the account seed. */
  export (password) {
    const salt = C.randomBytes(16)
    const key = C.passwordKey(password, salt)
    const body = C.encrypt(this.seed, key)
    return z32.encode(b4a.concat([b4a.from([VERSION]), salt, body]))
  }

  static import (blob, password, profile) {
    const raw = z32.decode(blob)
    if (raw[0] !== VERSION) throw new Error('Unsupported backup version')
    const salt = raw.subarray(1, 17)
    const key = C.passwordKey(password, salt)
    const seed = C.decrypt(raw.subarray(17), key)
    if (!seed) throw new Error('Wrong password or corrupted backup')
    return new Identity(seed, profile)
  }

  toPublic () {
    return { id: this.id, publicKey: this.publicKey, profile: { ...this.profile } }
  }
}

function deviceDelegationPayload (account, device, expires, label) {
  return C.domainHash('pine/device-delegation/v1', account, device,
    String(expires), label)
}

/**
 * Recovery key: the raw seed in z32, split into 4-character groups with a
 * 2-character checksum. Shorter and less error-prone to transcribe than a
 * 24-word mnemonic, and there is no word list to ship or localise.
 */
export function encodeRecoveryKey (seed) {
  const body = z32.encode(C.toBuffer(seed))
  const sum = z32.encode(C.domainHash('pine/recovery-checksum', seed).subarray(0, 2)).slice(0, 2)
  return (body + sum).toUpperCase().match(/.{1,4}/g).join('-')
}

export function decodeRecoveryKey (phrase) {
  const clean = String(phrase).toLowerCase().replace(/[^a-z0-9]/g, '')
  if (clean.length < 4) throw new Error('Recovery key is too short')
  const body = clean.slice(0, -2)
  const sum = clean.slice(-2)
  let seed
  try {
    seed = z32.decode(body)
  } catch {
    throw new Error('Recovery key contains invalid characters')
  }
  if (seed.byteLength !== C.SEED_BYTES) throw new Error('Recovery key has the wrong length')
  const expect = z32.encode(C.domainHash('pine/recovery-checksum', seed).subarray(0, 2)).slice(0, 2)
  if (expect !== sum) throw new Error('Recovery key checksum does not match — check for typos')
  return seed
}

export function toKey (value) {
  if (typeof value === 'string') {
    const s = value.startsWith('pine:') ? value.slice(5) : value
    return /^[0-9a-f]{64}$/i.test(s) ? b4a.from(s, 'hex') : z32.decode(s)
  }
  return C.toBuffer(value)
}

export function toId (key) {
  return z32.encode(toKey(key))
}
